import { DynamoDBClient, GetItemCommand, UpdateItemCommand } from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import { ENVIRONMENT_VARIABLES, NORMALIZED_STAGE } from 'src/constants/EnvironmentConstants';
import { CognitoService } from 'src/services/aws/CognitoService';

const TABLE_NAME = `finsuite_ea_huddle_employee_settings_${NORMALIZED_STAGE}`;

export interface UserEAConfig {
  leaderAlias: string;
  eaLogin?: string;
  updatedAt: string;
}

/**
 * Service for managing per-user EA overrides in DynamoDB.
 * Each user has their own config map, taking priority over the global LeaderEAConfigService.
 *
 * Schema:
 * - employee_alias: {userAlias}
 * - sk: USER_EA_CONFIGS
 * - configs: { [leaderAlias]: UserEAConfig }
 *
 * Note: Single-item approach is bounded by DDB's 400KB item limit.
 * At ~300 bytes per entry, this supports ~1000+ overrides per user comfortably.
 */
export class UserEAConfigService {
  private static client: DynamoDBClient | null = null;
  private static cache: Map<string, UserEAConfig[]> = new Map();
  private static cacheExpiry: Map<string, number> = new Map();
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000;

  private static getClient(): DynamoDBClient {
    if (this.client) return this.client;
    this.client = new DynamoDBClient({
      region: ENVIRONMENT_VARIABLES.env.Region,
      credentials: CognitoService.credentialProvider
    });
    return this.client;
  }

  /**
   * Get all user EA configs for a specific user. Cached in memory for 5 minutes.
   */
  public static async getAll(userAlias: string, skipCache = false): Promise<UserEAConfig[]> {
    const key = userAlias.toLowerCase();
    if (!skipCache && this.cache.has(key) && Date.now() < (this.cacheExpiry.get(key) ?? 0)) {
      return this.cache.get(key)!;
    }

    const client = this.getClient();
    const result = await client.send(
      new GetItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: key,
          sk: 'USER_EA_CONFIGS'
        })
      })
    );

    if (!result.Item) {
      this.cache.set(key, []);
      this.cacheExpiry.set(key, Date.now() + this.CACHE_TTL_MS);
      return [];
    }

    const item = unmarshall(result.Item);
    const configsMap = (item.configs ?? {}) as Record<string, Omit<UserEAConfig, 'leaderAlias'>>;
    const configs = Object.entries(configsMap).map(([alias, config]) => ({
      leaderAlias: alias,
      ...config
    })) as UserEAConfig[];

    this.cache.set(key, configs);
    this.cacheExpiry.set(key, Date.now() + this.CACHE_TTL_MS);
    return configs;
  }

  /**
   * Get all user EA configs synchronously from cache.
   */
  public static getAllCached(userAlias: string): UserEAConfig[] {
    return this.cache.get(userAlias.toLowerCase()) ?? [];
  }

  /**
   * Pre-load the cache for a user.
   */
  public static async preload(userAlias: string): Promise<void> {
    await this.getAll(userAlias);
  }

  /**
   * Find a user EA config by leader alias synchronously from cache.
   */
  public static findByLeaderCached(userAlias: string, leaderAlias: string): UserEAConfig | undefined {
    return this.getAllCached(userAlias).find((c) => c.leaderAlias.toLowerCase() === leaderAlias.toLowerCase());
  }

  /**
   * Save (create or update) a user EA config for a specific leader.
   * Bootstraps the parent item and `configs` map on first save (idempotent),
   * then atomically sets the entry. Two round-trips total — acceptable for
   * an infrequent user-driven write.
   */
  public static async save(userAlias: string, config: UserEAConfig): Promise<void> {
    const client = this.getClient();
    const key = userAlias.toLowerCase();
    const { leaderAlias, ...rest } = config;
    const leaderKey = leaderAlias.toLowerCase();

    // Step 1: Ensure parent item exists with a `configs` map. Idempotent —
    // `if_not_exists` is a no-op if `configs` already exists.
    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: key,
          sk: 'USER_EA_CONFIGS'
        }),
        UpdateExpression: 'SET configs = if_not_exists(configs, :empty)',
        ExpressionAttributeValues: marshall({ ':empty': {} })
      })
    );

    // Step 2: Set the entry under the now-guaranteed `configs` map.
    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: key,
          sk: 'USER_EA_CONFIGS'
        }),
        UpdateExpression: 'SET configs.#alias = :config, updatedAt = :now',
        ExpressionAttributeNames: { '#alias': leaderKey },
        ExpressionAttributeValues: marshall({ ':config': rest, ':now': new Date().toISOString() }, { removeUndefinedValues: true })
      })
    );
    this.invalidateCache(key);
  }

  /**
   * Delete a user EA config for a specific leader.
   * Uses atomic REMOVE on the map key, guarded by `attribute_exists(configs)`
   * to prevent creating an orphan item when the user has never saved any
   * configs. ConditionalCheckFailedException is treated as a no-op (the
   * override didn't exist anyway).
   */
  public static async delete(userAlias: string, leaderAlias: string): Promise<void> {
    const client = this.getClient();
    const key = userAlias.toLowerCase();
    const leaderKey = leaderAlias.toLowerCase();

    try {
      await client.send(
        new UpdateItemCommand({
          TableName: TABLE_NAME,
          Key: marshall({
            employee_alias: key,
            sk: 'USER_EA_CONFIGS'
          }),
          UpdateExpression: 'SET updatedAt = :now REMOVE configs.#alias',
          ConditionExpression: 'attribute_exists(configs)',
          ExpressionAttributeNames: { '#alias': leaderKey },
          ExpressionAttributeValues: marshall({ ':now': new Date().toISOString() })
        })
      );
    } catch (error) {
      if ((error as { name: string }).name === 'ConditionalCheckFailedException') {
        // Item or configs map doesn't exist — nothing to delete, treat as success.
        return;
      }
      throw error;
    } finally {
      this.invalidateCache(key);
    }
  }

  /** Invalidate the in-memory cache for a user. */
  public static invalidateCache(userAlias: string): void {
    const key = userAlias.toLowerCase();
    this.cache.delete(key);
    this.cacheExpiry.delete(key);
  }
}
